// src/components/WeeklyTrailerCalendar/WeeklyTrailerCalendar.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";

import PropTypes from "prop-types";
import { useBooking } from "../../context/BookingContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import {
  MdCalendarToday,
  MdArrowBack,
  MdArrowForward,
  MdKeyboardArrowUp,
  MdCheckCircle,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";

/**
 * Tarkistaa, meneekö aikavälit (start1–end1) ja (start2–end2) päällekkäin.
 */
const isOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

/**
 * Palauttaa saman viikon maanantain annetusta päivämäärästä.
 */
const getCurrentWeekMonday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getMonthNumber = (date) => {
  const month = date.getMonth() + 1; // 0–11 → 1–12
  return String(month).padStart(2, "0");
};


/**
 * Muodostaa tunnin merkkijonoksi hh:mm.
 */
const formatSlotTime = (hour) => String(hour).padStart(2, "0") + ":00";

/**
 * Lisää 'days' päivää annettuun päivämäärään.
 */
const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

/**
 * Lasketaan ISO-standardin mukainen viikkonumero.
 */
const getISOWeekNumber = (date) => {
  const temp = new Date(date.getTime());
  temp.setHours(0, 0, 0, 0);
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7));
  const year = temp.getFullYear();
  const firstThursday = new Date(year, 0, 4);
  const dayOfYear = Math.round((temp - firstThursday) / 86400000) + 1;
  return 1 + Math.floor((dayOfYear - 1) / 7);
};


/**
 * Traileria vastaavat avainsanat
 */
const trailerKeywords = [
  "peräkärry",
  "perakarry",
  "traileri",
  "hirvi 350",
  "kuomukärry",
  "kärry",
  "karry",
];

const WeeklyTrailerCalendar = ({ reservations }) => {
  const { selectedDates, setSelectedDates, setTrailerPickupTime } = useBooking();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("trailerPickupTime")) {
      localStorage.setItem("trailerPickupTime", "07:00");
    }
  }, []);

  /**
   * Määritellään weekStart:
   *  - Jos selectedDates.startDate on asetettu → käytetään sitä
   *  - Muuten käytetään tämän päivän aikaa
   *  - Ja haetaan kuluvan viikon maanantai.
   */
  const [weekStart, setWeekStart] = useState(() => {
    const baseDate = selectedDates?.startDate ? new Date(selectedDates.startDate) : new Date();
    return getCurrentWeekMonday(baseDate);
  });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Käytetään absoluuttisia varausaikoja
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // Myös suhteelliset valinnat (voit käyttää niitä, jos solujen korostuksessa halutaan säilyttää UI:n logiikkaa)
  const [startPick, setStartPick] = useState(null);


  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Näytetään vain ajat 06:00–00:00 (eli 6–23)
  const slotsArray = Array.from({ length: 18 }, (_, i) => i + 6);

  const hasTrailer = useMemo(() => reservations.length > 0, [reservations]);

  // Asetetaan selectedDates localStoragesta, jos niitä ei vielä ole
  useEffect(() => {
    if (!localStorage.getItem("selectedBookingDates")) {
      const defaultDates = {
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 86400000).toISOString(),
      };
      setSelectedDates(defaultDates);
      localStorage.setItem("selectedBookingDates", JSON.stringify(defaultDates));
    }
  }, [setSelectedDates]);

  const getCellDate = useCallback((dayIndex, hour) => {
    const cellDate = new Date(weekDays[dayIndex]);
    cellDate.setHours(hour, 0, 0, 0);
    return cellDate;
  }, [weekDays]);

  useEffect(() => {
    const savedDates = JSON.parse(localStorage.getItem("selectedBookingDates"));
    if (savedDates?.startDate && savedDates?.endDate) {
      setSelectedDates(savedDates);
    }
  }, [setSelectedDates]);

  useEffect(() => {
    if (hasTrailer && selectedDates?.startDate) {
      const start = new Date(selectedDates.startDate);
      const hh = String(start.getHours()).padStart(2, "0");
      const mm = String(start.getMinutes()).padStart(2, "0");
      setStartTime(`${hh}:${mm}`);
      setTrailerPickupTime(`${hh}:${mm}`);
    }
  }, [hasTrailer, selectedDates?.startDate, setTrailerPickupTime]);

  // Tämä sulla on jo:
useEffect(() => {
  if (hasTrailer && selectedDates?.startDate) {
    const start = new Date(selectedDates.startDate);
    const hh = String(start.getHours()).padStart(2, "0");
    const mm = String(start.getMinutes()).padStart(2, "0");
    setStartTime(`${hh}:${mm}`);
    setTrailerPickupTime(`${hh}:${mm}`);
  }
}, [hasTrailer, selectedDates?.startDate, setTrailerPickupTime]);

// 🔽 TÄMÄ LISÄTÄÄN HETI TÄMÄN PERÄÄN:
useEffect(() => {
  if (startDateTime || endDateTime || reservations.length === 0) return;

  const wasCleared = sessionStorage.getItem("trailerSelectionCleared") === "true";
  if (wasCleared) return;

  const now = new Date();
  for (let dayIdx = 0; dayIdx < weekDays.length; dayIdx++) {
    for (let hour = 6; hour <= 19; hour++) {
      const start = getCellDate(dayIdx, hour);
      const end = new Date(start.getTime() + 4 * 60 * 60 * 1000); // +4h

      if (start < now) continue;

      const slotIsFree = !reservations.some((res) =>
        isOverlap(new Date(res.start), new Date(res.end), start, end)
      );

      if (slotIsFree) {
        setStartDateTime(start);
        setEndDateTime(end);
        setStartPick({ dayIndex: dayIdx, slot: hour });

        setStartTime(formatSlotTime(start.getHours()));
        setEndTime(formatSlotTime(end.getHours()));
        return;
      }
    }
  }
}, [reservations, weekDays, startDateTime, endDateTime, getCellDate]);






  // Päivitetään näytettävät kellonajat absoluuttisista varausajoista
  useEffect(() => {
    if (startDateTime) {
      setStartTime(formatSlotTime(startDateTime.getHours()));
    } else {
      setStartTime("");
    }
    if (endDateTime) {
      setEndTime(formatSlotTime(endDateTime.getHours()));
    } else {
      setEndTime("");
    }
  }, [startDateTime, endDateTime]);

  // Lasketaan vuokrauksen kesto absoluuttisista ajoista
  const selectedDuration = useMemo(() => {
    if (!startDateTime || !endDateTime) return null;
    const msDiff = endDateTime - startDateTime;
    if (msDiff <= 0) return null;
    const totalHours = msDiff / 3600000;
    const days = Math.floor(totalHours / 24);
    const hours = Math.round(totalHours % 24);
    return { days, hours };
  }, [startDateTime, endDateTime]);


  // Klikkaustoiminto: asetetaan absoluuttiset varausaikat ja suhteelliset solukuviot
  const handleSlotClick = (dayIndex, hour) => {
    const clickedDate = getCellDate(dayIndex, hour);
    // Jos klikataan samaa solua kuin aloitusaika, resetoi varaus
    if (startPick && startPick.dayIndex === dayIndex && startPick.slot === hour) {
      setStartPick(null);
   
      setStartDateTime(null);
      setEndDateTime(null);
      return;
    }
    // Jos aloitusaikaa ei ole asetettu, asetetaan se
    if (!startDateTime) {
      setStartDateTime(clickedDate);
      setStartPick({ dayIndex, slot: hour });
      setEndDateTime(null);

      return;
    }
    // Jos klikattu aika on ennen nykyistä aloitusaikaa, päivitetään aloitusaika (resetoi myös lopetus)
    if (clickedDate < startDateTime) {
      setStartDateTime(clickedDate);
      setStartPick({ dayIndex, slot: hour });
      setEndDateTime(null);

    } else {
      // Muussa tapauksessa asetetaan lopetusaika
      setEndDateTime(clickedDate);
    
    }
  };

  // Reset-nappi
  const clearSelection = () => {
    setStartDateTime(null);
    setEndDateTime(null);
    setStartPick(null);
    setStartTime("");
    setEndTime("");
    setSelectedDates(null);
    localStorage.removeItem("selectedBookingDates");
  
    sessionStorage.setItem("trailerSelectionCleared", "true");
  };
  
  

  // Varauksen tekeminen: käytetään absoluuttisia varausaikoja
  const handleReserve = () => {
    if (!startDateTime || !endDateTime) return;
    const durationHours = (endDateTime - startDateTime) / 3600000;
    if (durationHours < 4) {
      toast.error("Peräkärryä ei voi vuokrata alle 4 tunnin ajaksi.");
      return;
    }
    setSelectedDates({
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
    });
    localStorage.setItem(
      "selectedBookingDates",
      JSON.stringify({
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
      })
    );
    const success = addTrailerToCart(startDateTime, endDateTime);
    if (success) {
      navigate("/tee-varaus");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const addTrailerToCart = (start, end) => {
    const trailerItem = {
      id: 24,
      name: "HIRVI 350",
      slug: "hirvi-350",
      price: 25,
      quantity: 1,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      vaatii_ajan: 1,
    };
    const cart = JSON.parse(localStorage.getItem("rentalCart")) || [];
    const hasOtherThanTrailer = cart.some((item) =>
      trailerKeywords.every(
        (kw) =>
          !item.name?.toLowerCase().includes(kw) &&
          !item.slug?.toLowerCase().includes(kw)
      )
    );
    if (hasOtherThanTrailer) {
      toast.error("Peräkärryn kanssa ei voi vuokrata muita tuotteita.");
      return false;
    }
    const newCart = cart.filter((c) => c.id !== 24);
    newCart.push(trailerItem);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
    window.dispatchEvent(new Event("cartUpdated"));
    return true;
  };

  const isSlotReserved = (dayIndex, hour) => {
    const d = getCellDate(dayIndex, hour);
    const dEnd = new Date(d);
    dEnd.setHours(dEnd.getHours() + 1);
    return reservations.some((res) => {
      const rStart = new Date(res.start);
      const rEnd = new Date(res.end);
      return isOverlap(rStart, rEnd, d, dEnd);
    });
  };

  const isSlotInRange = (dayIndex, hour) => {
    if (!startDateTime || !endDateTime) return false;
    const cellDate = getCellDate(dayIndex, hour);
    return cellDate >= startDateTime && cellDate < endDateTime;
  };

  const weekNumber = getISOWeekNumber(weekStart);

  const handlePrevWeek = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const prevMonday = addDays(weekStart, -7);
    if (prevMonday < today) {
      toast.info("Et voi tarkastella menneitä!");
      return;
    }
    setWeekStart(prevMonday);
  };

  const isRangeAvailable = () => {
    if (!startDateTime || !endDateTime) return false;
    const msDiff = endDateTime - startDateTime;
    // Varmistetaan, että varauksen kesto on vähintään 4 tuntia
    return msDiff >= 4 * 3600000;
  };
  

  const handleNextWeek = () => {
    setWeekStart((old) => addDays(old, 7));
  };

  // RENDERÖINTI:
  return (
    <div className="my-10 px-4 sm:px-6 lg:px-8 xl:px-20 2xl:px-32 max-w-screen-xl mx-auto p-5 dark:bg-violet bg-primary dark:bg-opacity-75 bg-opacity-75 flex gap-4 flex-col rounded-3xl">
      <h2 className="font-customFont2 dark:text-shadow-lg text-center text-darkest dark:text-primary text-4xl sm:text-4xl md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-4">
        {`Vuokrauskalenteri – viikko ${weekNumber}`}
      </h2>

      <div className="flex flex-col md:flex-row mdd:flex-row lg:flex-row xl:flex-row 2xl:flex-row flex-wrap justify-center items-center gap-3 mb-6 text-center">
        {weekStart > getCurrentWeekMonday() ? (
          <ResponsiveButton
            className="w-full md:w-40 mdd:w-40 lg:w-40 xl:w-32 2xl:w-36 font-customFont1 text-sm font-bold"
            onClick={() => setWeekStart(getCurrentWeekMonday())}
          >
            Palaa tähän viikkoon
          </ResponsiveButton>
        ) : (
          <ResponsiveButton
            className="w-full md:w-40 mdd:w-40 lg:w-32 xl:w-32 xl:text-sm 2xl:w-36 font-customFont1 text-sm font-bold"
            onClick={handlePrevWeek}
            icon={MdArrowBack}
            disabled={true}
          >
            Edellinen viikko
          </ResponsiveButton>
        )}

        <span className="font-customFont1 dark:text-shadow-lg text-darkest dark:text-primary font-light w-auto text-sm">
          {weekDays[0].toLocaleDateString("fi-FI")} – {weekDays[6].toLocaleDateString("fi-FI")}
        </span>

        <ResponsiveButton
          className="w-full md:w-40 mdd:w-40 lg:w-32 xl:w-32 2xl:w-36 font-customFont1 text-sm font-bold"
          onClick={handleNextWeek}
          icon={MdArrowForward}
        >
          Seuraava viikko
        </ResponsiveButton>
      </div>

      <div className="relative flex justify-center mt-10 md:hidden mdd:hidden lg:hidden xl:hidden 2xl:hidden">
        <div className="absolute -top-10 animate-bounce text-5xl dark:text-white">
          <MdKeyboardArrowUp />
        </div>
        <ResponsiveButton
          onClick={() => {
            const el = document.getElementById("trailerinfo");
            if (el) {
              const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
              window.scrollTo({ top: y, behavior: "smooth" });
            }
          }}
          variant="scrolltotop"
          className="flex items-center gap-2 w-52"
        >
          Katso kärryn tiedot
        </ResponsiveButton>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 text-center">
        <div className="flex flex-col w-full sm:w-1/3">
          <label
            htmlFor="startTime"
            className="mb-1 font-customFont1 dark:text-shadow-lg text-darkest dark:text-primary xxxsm:text-lg text-lg sm:text-lg md:text-lg lg:text-lg xl:text-xl font-bold text-center"
          >
            Alkuaika
          </label>
          <input
            id="startTime"
            type="text"
            readOnly
            value={startTime}
            className="w-full text-center cursor-default font-customFont1 bg-white dark:bg-darkest dark:text-primary border border-gray-800 rounded-xl shadow-sm text-lg"
          />
        </div>
        <div className="flex flex-col w-full sm:w-1/3">
          <label
            htmlFor="endTime"
            className="mb-1 font-customFont1 dark:text-shadow-lg text-darkest dark:text-primary xxxsm:text-lg text-lg sm:text-lg md:text-lg lg:text-lg xl:text-xl font-bold text-center"
          >
            Loppuaika
          </label>
          <input
            id="endTime"
            type="text"
            readOnly
            value={endTime}
            className="w-full text-center cursor-default font-customFont1 bg-white dark:bg-darkest dark:text-primary border border-gray-800 rounded-xl shadow-sm text-lg"
          />
        </div>
      </div>

      <div className="w-full flex justify-center">
        <ResponsiveButton
          onClick={clearSelection}
          variant="cancel"
          className="w-56 font-customFont1 text-sm font-semibold"
        >
          Tyhjennä valinta
        </ResponsiveButton>
      </div>


      <div className="text-center mb-6 bg-linkcol dark:bg-violet bg-opacity-85 dark:bg-opacity-85 p-4 rounded-md">
        {(!startDateTime || !endDateTime) ? (
          <p className="font-customFont1 dark:text-shadow-lg text-center text-white dark:text-primary xxxsm:text-lg text-lg font-normal mb-3">
            Kalenteri ehdottaa ensimmäisellä kerralla automaattisesti ensimmäistä vapaata 4 tunnin ajanjaksoa.
            <br />
            Voit laajentaa varausta pidemmäksi valitsemalla myöhemmän loppuajan kalenterista.
            <br /> <br />
            Voit tyhjentää valinnan kokonaan &quot;Tyhjennä valinta&quot;-napista.
            <br />
            <strong>Alle 4 tunnin varauksia ei voi tehdä.</strong>
            <br />
            Punainen väri tarkoittaa, että kärry on jo varattu kyseiselle ajalle.
          </p>


       
        ) : (
          <>
            {isRangeAvailable() ? (
              <div className="font-customFont1 dark:text-shadow-lg text-center text-white dark:text-primary xxxsm:text-lg text-lg font-light mb-3">
                <p className="inline-flex items-center justify-center gap-1">
                  Aikaväli on vapaa
                  <span className="text-green-400 animate-pulse">
                    <MdCheckCircle size={20} />
                  </span>
                </p>
                <br />
                {/* Näytetään absoluuttiset ajat */}
                Alku: {startDateTime.toLocaleDateString("fi-FI", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}{" "}
                {formatSlotTime(startDateTime.getHours())}
                <br />
                Loppu: {endDateTime.toLocaleDateString("fi-FI", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}{" "}
                {formatSlotTime(endDateTime.getHours())}
                <br />
                {selectedDuration && (
                  <span className="text-sm font-normal italic block mt-1">
                    Kesto:{" "}
                    {selectedDuration.days > 0 ? `${selectedDuration.days} pv ` : ""}
                    {selectedDuration.hours > 0 ? `${selectedDuration.hours} h` : ""}
                  </span>
                )}
              </div>
            ) : (
              <p className="font-customFont1 dark:text-shadow-lg text-center text-red-600 xxxsm:text-lg text-lg font-bold mb-3">
                Aikaväli ei ole saatavilla ❌
              </p>
            )}
          </>
        )}
        <div className="flex justify-center">
          <ResponsiveButton
            className="w-72 font-customFont1 text-sm font-bold"
            onClick={handleReserve}
            disabled={!startDateTime || !endDateTime || !isRangeAvailable()}
            icon={MdCalendarToday}
          >
            Varaa tämä aika
          </ResponsiveButton>
        </div>
      </div>

      <div className="overflow-auto">
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((day, dayIdx) => (
            <div
              key={dayIdx}
              className="font-customFont1 dark:text-shadow-lg text-center text-darkest dark:text-primary font-bold text-xs py-2"
            >
             {day.toLocaleDateString("fi-FI", {
                weekday: "short",
              })} {day.getDate().toString().padStart(2, "0")}.{getMonthNumber(day)}
            </div>
          ))}
        </div>
        {slotsArray.map((hour) => (
  <div key={hour} className="grid grid-cols-7 gap-1 mb-1">
    {weekDays.map((day, dayIndex) => {
      const cellDate = new Date(day);
      cellDate.setHours(hour, 0, 0, 0);
      const now = new Date();
      const isPast = cellDate < now;
      const slotReserved = isSlotReserved(dayIndex, hour);
      const slotSelected = isSlotInRange(dayIndex, hour);
      const slotIsStart = startDateTime && cellDate.getTime() === startDateTime.getTime();
      // Käytetään absoluuttista endDateTimea lopetusaikaan
      const slotIsEnd = endDateTime && cellDate.getTime() === endDateTime.getTime();

      let bgClass = "bg-slot-default dark:hover:bg-purple-600 hover:bg-linkcol";
      let cursorClass = "cursor-pointer";
      let extraClass = "";

      if (isPast) {
        bgClass = "bg-gray-300 dark:bg-gray-700";
        cursorClass = "cursor-not-allowed";
      } else if (slotReserved) {
        bgClass = "bg-slot-reserved";
        cursorClass = "cursor-not-allowed";
      } else if (slotIsStart && slotIsEnd) {
        bgClass = "bg-slot-startend";
      } else if (slotIsStart) {
        bgClass = "bg-slot-start";
      } else if (slotIsEnd) {
        bgClass = "bg-slot-end";
      } else if (slotSelected) {
        bgClass = "bg-slot-range animate-shimmer bg-[length:600%_100%] bg-shimmer";
        extraClass = "text-primary";
      }

      return (
        <button
          key={`${hour}-${dayIndex}`}
          type="button"
          onClick={() => {
            if (!slotReserved && !isPast) {
              handleSlotClick(dayIndex, hour);
            }
          }}
          className={`py-1 text-xs font-medium transition-colors rounded-sm ${bgClass} ${cursorClass} ${extraClass} font-customFont1 text-center text-darkest xxxsm:text-lg text-lg font-light`}
          title={
            isPast
              ? "Aika on mennyt"
              : slotReserved
              ? "Aikaväli on varattu"
              : "Klikkaa valitaksesi tämä tunti"
          }
        >
          {formatSlotTime(hour)}
        </button>
      );
    })}
  </div>
))}

      </div>
    </div>
  );
};

WeeklyTrailerCalendar.propTypes = {
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default WeeklyTrailerCalendar;
